import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, PlayCircle } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.12, ease: "easeOut" } }),
};

export const HeroSection = () => (
  <section className="relative min-h-screen flex items-center lp-grid-lines" data-testid="hero-section">
    <div className="absolute inset-0 bg-gradient-to-b from-white via-white/40 to-[#fafbff]" />
    <div className="lp-orb w-[550px] h-[450px] bg-sky-300/50 top-16 -left-40" />
    <div className="lp-orb w-[500px] h-[420px] bg-indigo-300/40 bottom-0 -right-24" style={{ animationDelay: "3s" }} />

    <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-10 pt-36 md:pt-44 pb-20 w-full">
      <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0} className="mb-7">
        <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-1.5 text-xs md:text-sm font-semibold uppercase tracking-[0.2em] text-blue-700" data-testid="hero-overline">
          The Premium WMS for 3PLs
        </span>
      </motion.div>
      <motion.h1
        variants={fadeUp} initial="hidden" animate="show" custom={1}
        className="lp-display text-4xl sm:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.02] max-w-5xl text-[#0b1530]"
        data-testid="hero-headline"
      >
        Precision Logistics.
        <br />
        <span className="lp-gradient-text">Unbound Scale.</span>
      </motion.h1>
      <motion.p
        variants={fadeUp} initial="hidden" animate="show" custom={2}
        className="mt-8 text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl"
        data-testid="hero-subtext"
      >
        One multi-tenant command center for every warehouse you run. Orders, inbound,
        inventory, returns and invoicing — isolated per tenant, governed by 8 precise roles,
        audited on every move.
      </motion.p>
      <motion.div
        variants={fadeUp} initial="hidden" animate="show" custom={3}
        className="mt-11 flex flex-wrap items-center gap-4"
      >
        <Link
          to="/contact"
          className="group inline-flex items-center gap-2.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-base md:text-lg font-semibold px-8 py-4 transition-colors shadow-xl shadow-blue-600/30"
          data-testid="hero-cta-demo"
        >
          Request a Demo
          <ArrowRight size={19} strokeWidth={2} className="transition-transform group-hover:translate-x-1" />
        </Link>
        <Link
          to="/login"
          className="inline-flex items-center gap-2.5 rounded-full border-2 border-slate-300 hover:border-blue-500 bg-white text-slate-700 hover:text-blue-600 text-base md:text-lg font-semibold px-8 py-[0.9rem] transition-colors"
          data-testid="hero-cta-signin"
        >
          <PlayCircle size={19} strokeWidth={2} />
          Sign In
        </Link>
      </motion.div>
      <motion.div
        variants={fadeUp} initial="hidden" animate="show" custom={4}
        className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-2xl overflow-hidden max-w-4xl shadow-xl shadow-blue-600/5"
        data-testid="hero-stats"
      >
        {[
          ["10", "Operational modules"],
          ["8", "Governed roles"],
          ["100%", "Tenant isolation"],
          ["Live", "Audit trail"],
        ].map(([v, l]) => (
          <div key={l} className="bg-white px-6 py-6">
            <p className="lp-display text-3xl md:text-4xl font-semibold lp-gradient-text">{v}</p>
            <p className="text-sm text-slate-500 mt-1.5">{l}</p>
          </div>
        ))}
      </motion.div>
    </div>
  </section>
);
